import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  makeStyles,
  Paper,
  TextField,
  Typography,
  Divider,
  InputAdornment,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { Info } from "@material-ui/icons";

import { green } from "@material-ui/core/colors";
import React, { useState, useEffect } from "react";
import axios from "axios";

import {
  Edit,
  Visibility,
  Search as SearchIcon,
  ArrowDropDown,
  Refresh,
  ChevronLeft,
  ChevronRight,
} from "@material-ui/icons";
import { PDFViewer } from "../../sasl/batch/updateBatch/StructuredInformation/PDFViewer";
import { appUrl } from "../../../../../../utils/config";


const initialValuesFromDb = {
  invoice_number: {
    value: "24066471 RI",
    confidence: "99.4958724975586"
  },
  invoice_date: {
    value: "20-May-2025",
    confidence: "99.83318328857422"
  },
  item_number: {
    value: "50066006",
    confidence: "99.93083953857422"
  },
  product_description: {
    value: "BARACLUDE TAB 0.5MG (3BLP X 10) FR",
    confidence: "97.12879180908203"
  },
  quantity: {
    value: "3650.0000",
    confidence: "100.0"
  },
  document_type: "CommercialInvoice",
  folder_name: "PCI",
  site_name: "Dublin",
  filename: "PCI-Dublin-CommercialInvoice-4500023887-BatchTest2A.pdf",
  s3_location: "s3://bms-processed-files/PCI/Dublin/4500023887-BatchTest2A/PCI-Dublin-CommercialInvoice-4500023887-BatchTest2A.pdf"
};

// styling
const useStyles = makeStyles((theme) => ({
  formLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    minWidth: "150px",
    paddingRight: theme.spacing(2),
    textAlign: "right",
  },
  fixedLabel: {
    width: 180,
    textAlign: "left",
    paddingRight: theme.spacing(2),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  requiredStar: {
    color: "red",
    marginLeft: 4,
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  testTable: {
    '& .MuiTableCell-head': {
      backgroundColor: '#f5f5f5',
      fontWeight: 'bold',
    },
  },
}));

const DigitizedCommercialInvoice = (props) => {
  const { initialData, onClose, schrockenCustomFileID, digitizedData } = props;
  const [isEditable, setIsEditable] = useState(false);
  const [formValues, setFormValues] = useState(initialData || {});
  const [originalValues, setOriginalValues] = useState(initialData || {});
  const [editedValues, setEditedValues] = useState(initialData || {});
  const [showingOriginal, setShowingOriginal] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [modalData, setModalData] = useState({
    field: "",
    before: "",
    after: "",
    updatedBy: "user123",
    timestamp: new Date().toLocaleString(),
  });

  const classes = useStyles();

  useEffect(() => {
    const dataToUse = initialData || {};
    setFormValues(dataToUse);
    setOriginalValues(dataToUse);
    setEditedValues(dataToUse);
  }, [initialData]);

  // Validation functions based on data types
  const validateField = (fieldKey, value) => {
    const validations = {
      invoice_number: {
        type: "Alphanumeric",
        validate: (val) => {
          if (!val || val.trim() === '') return "CMO Invoice # is required";
          if (!/^[A-Za-z0-9\s]+$/.test(val)) return "Must be alphanumeric";
          return null;
        }
      },
      invoice_date: {
        type: "Date DD-MMM-YYYY",
        validate: (val) => {
          if (!val || val.trim() === '') return "CMO Invoice Date is required";
          if (!/^\d{1,2}-[A-Za-z]{3}-\d{4}$/.test(val)) return "Must be in DD-MMM-YYYY format";
          return null;
        }
      },
      item_number: {
        type: "Numeric",
        validate: (val) => {
          if (!val || val.trim() === '') return "BMS SKU is required";
          if (!/^\d+$/.test(val)) return "Must be numeric";
          return null;
        }
      },
      product_description: {
        type: "Text",
        validate: (val) => {
          if (!val || val.trim() === '') return "BMS Description is required";
          if (val.length < 5) return "Description should be more detailed";
          return null;
        }
      },
      quantity: {
        type: "Decimal",
        validate: (val) => {
          if (!val || val.trim() === '') return "Actual Component Quantity Consumed is required";
          if (!/^\d+(\.\d+)?$/.test(val)) return "Must be a numeric value";
          return null;
        }
      }
    };

    const fieldValidation = validations[fieldKey];
    if (fieldValidation) {
      return fieldValidation.validate(value);
    }
    return null;
  };

  const validateAllFields = () => {
    const errors = {};
    Object.keys(formValues).forEach(fieldKey => {
      const error = validateField(fieldKey, getValueForField(fieldKey));
      if (error) {
        errors[fieldKey] = error;
      }
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getConfidenceForField = (fieldKey) => {
    const fieldData = formValues[fieldKey];
    if (fieldData && fieldData.confidence) {
      return parseFloat(fieldData.confidence);
    }
    return 90; // Default fallback
  };

  const getValueForField = (fieldKey, dataSource = formValues) => {
    const fieldData = dataSource[fieldKey];
    if (fieldData && typeof fieldData === 'object' && fieldData.value !== undefined) {
      return fieldData.value || '';
    }
    return fieldData || '';
  };

  const getLevelColor = (level) => {
    if (level < 80) return "red";
    if (level <= 90) return "orange";
    return "green";
  };

  const handleInputChange = (fieldKey, value) => {
    const newValues = {
      ...formValues,
      [fieldKey]: {
        ...formValues[fieldKey],
        value: value
      }
    };
    setFormValues(newValues);
    if (!showingOriginal) {
      setEditedValues(newValues);
    }

    // Validate the field and update errors
    const error = validateField(fieldKey, value);
    setValidationErrors(prev => ({
      ...prev,
      [fieldKey]: error
    }));
  };

  const handleSave = async () => {
    if (validateAllFields()) {
      try {
        setSaving(true);
        
        // Parse existing kvpJson and update with current form values
        const existingKvpJson = digitizedData?.kvpJson ? JSON.parse(digitizedData.kvpJson) : {};
        
        // Update kvpJson with current form values
        const updatedKvpJson = {
          ...existingKvpJson,
          invoice_number: {
            value: getValueForField('invoice_number'),
            confidence: existingKvpJson.invoice_number?.confidence || 90
          },
          invoice_date: {
            value: getValueForField('invoice_date'),
            confidence: existingKvpJson.invoice_date?.confidence || 90
          },
          item_number: {
            value: getValueForField('item_number'),
            confidence: existingKvpJson.item_number?.confidence || 90
          },
          product_description: {
            value: getValueForField('product_description'),
            confidence: existingKvpJson.product_description?.confidence || 90
          },
          quantity: {
            value: getValueForField('quantity'),
            confidence: existingKvpJson.quantity?.confidence || 90
          }
        };

        // Prepare API request payload
        const requestPayload = {
          vendorMosymphonyID: digitizedData?.vendorMosymphonyID || 3,
          digitizedExtractedDataList: [{
            poNumber: digitizedData?.poNumber,
            batchNumber: digitizedData?.batchNumber,
            processedFileName: digitizedData?.processedFileName,
            senderEmailID: digitizedData?.senderEmailID,
            emailReceivedTimestamp: digitizedData?.emailReceivedTimestamp,
            attachmentName: digitizedData?.attachmentName,
            documentType: digitizedData?.documentType,
            documentFormat: digitizedData?.documentFormat,
            emailSubject: digitizedData?.emailSubject || "",
            emailBody: digitizedData?.emailBody || "",
            schrockenCustomFileID: digitizedData?.schrockenCustomFileID,
            kvpJson: JSON.stringify(updatedKvpJson),
            siteName: digitizedData?.siteName
          }]
        };

        // Make API call
        const response = await axios.post(
          `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateDigitizedExtractedData`,
          requestPayload
        );

        if (response.data.status) {
          // Save changes locally
          setEditedValues(formValues);
          setIsEditable(false);
          setShowingOriginal(false);
          
          // Show success message or callback
          //console.log('Commercial Invoice data saved successfully');
        } else {
          console.error('Failed to save:', response.data.message);
          // Handle error - could show error message to user
        }
      } catch (error) {
        console.error('Error saving Commercial Invoice data:', error);
        // Handle error - could show error message to user
      } finally {
        setSaving(false);
      }
    }
  };

  const getFieldErrorProps = (fieldKey) => ({
    error: !!validationErrors[fieldKey],
    helperText: validationErrors[fieldKey]
  });

  const renderField = (fieldKey, label, placeholder, multiline = false, rows = 1) => (
    <Grid
      container
      alignItems="center"
      spacing={2}
      className={classes.formField}
    >
      <Grid item xs={4}>
        <Box display="flex" alignItems="center">
          <Typography
            style={{
              paddingRight: 16,
              fontWeight: 500,
            }}
          >
            {label}
            <span className={classes.requiredStar}>*</span>
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={8}>
        <TextField
          variant="outlined"
          fullWidth
          size="small"
          value={getValueForField(fieldKey)}
          onChange={(e) =>
            handleInputChange(fieldKey, e.target.value)
          }
          onBlur={(e) => {
            const { value } = e.target;
            // Trim leading and trailing spaces for string values
            if (typeof value === "string" && value.trim() !== value) {
              handleInputChange(fieldKey, value.trim());
            }
          }}
          placeholder={placeholder}
          multiline={multiline}
          rows={rows}
          {...getFieldErrorProps(fieldKey)}
          InputProps={{
            readOnly: !isEditable,
            endAdornment: (
              <InputAdornment position="end">
                <Box display="flex" alignItems="center" pl={1}>
                  <Box
                    style={{
                      width: 1,
                      height: multiline ? 50 : 39,
                      backgroundColor: "#ccc",
                      marginRight: 3,
                    }}
                  />
                  {!isEditable ? (
                    getValueForField(fieldKey) !== getValueForField(fieldKey, originalValues) ? (
                      <Box
                        onClick={() => {
                          setModalData({
                            field: label,
                            before: getValueForField(fieldKey, originalValues),
                            after: getValueForField(fieldKey),
                            updatedBy: "user123",
                            timestamp:
                              new Date().toLocaleString(),
                          });
                          setOpenModal(true);
                        }}
                        style={{
                          backgroundColor: "#7777",
                          color: "white",
                          borderRadius: 4,
                          padding: "2px 8px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          minWidth: 24,
                          textAlign: "center",
                          cursor: "pointer",
                        }}
                      >
                        <Info fontSize="small" />
                      </Box>
                    ) : (
                      <Box
                        style={{
                          backgroundColor: getLevelColor(getConfidenceForField(fieldKey)),
                          color: "white",
                          borderRadius: 4,
                          padding: "2px 8px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          minWidth: 24,
                          textAlign: "center",
                        }}
                      >
                        {Math.round(getConfidenceForField(fieldKey))}
                      </Box>
                    )
                  ) : (
                    <Box
                      style={{
                        backgroundColor: getLevelColor(getConfidenceForField(fieldKey)),
                        color: "white",
                        borderRadius: 4,
                        padding: "2px 8px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        minWidth: 24,
                        textAlign: "center",
                      }}
                    >
                      {Math.round(getConfidenceForField(fieldKey))}
                    </Box>
                  )}
                </Box>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
    </Grid>
  );

  return (
    <>
      <Grid container direction="column" spacing={2}>

        {/* Actions*/}
        <Grid item container alignItems="center" spacing={2}>
          <Grid item xs={6}>
            <Grid item style={{display: "flex", alignItems: "center"}} spacing={2}>
              <Typography
                variant="h6"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  marginRight: "8px",
                }}
              >
                Commercial Invoice Data
              </Typography>
              <Box display="flex" gap={1} spacing={2}>
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={() => {
                    if (isEditable) {
                      handleSave();
                    } else {
                      // Start editing
                      setIsEditable(true);
                      setShowingOriginal(false);
                      setFormValues(editedValues);
                    }
                  }}
                  style={{ marginRight: "8px", minWidth: 15, textAlign: "center" }}
                  disabled={saving}
                >
                  {saving ? "Saving..." : isEditable ? "Save Changes" : "Edit"}
                </Button>
                <Button
                  color="secondary"
                  variant="outlined"
                  onClick={() => {
                    if (showingOriginal) {
                      // Show edited version
                      setFormValues(editedValues);
                      setShowingOriginal(false);
                    } else {
                      // Show original version
                      setFormValues(originalValues);
                      setShowingOriginal(true);
                    }
                    setIsEditable(false);
                  }}
                  style={{ marginRight: "8px", minWidth: 20, textAlign: "center", size: "20px" }}
                >
                  {showingOriginal ? "Show Edited Version" : "Show Unedited Version"}
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6">PDF Preview</Typography>
          </Grid>
        </Grid>

        <Grid item container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              style={{
                height: "calc(100vh - 200px)",
                padding: 16,
                overflowY: "auto",
              }}
            >
              {/* CMO Invoice # */}
              {renderField('invoice_number', 'CMO Invoice #', 'Enter CMO Invoice #')}

              {/* CMO Invoice Date */}
              {renderField('invoice_date', 'CMO Invoice Date', 'Enter CMO Invoice Date (DD-MMM-YYYY)')}

              {/* BMS SKU */}
              {renderField('item_number', 'BMS SKU', 'Enter BMS SKU')}

              {/* BMS Description */}
              {renderField('product_description', 'BMS Description', 'Enter BMS Description', true, 2)}

              {/* Actual Component Quantity Consumed */}
              {renderField('quantity', 'Actual Component Quantity Consumed', 'Enter Actual Component Quantity Consumed')}

              <Box display="flex" alignItems="center" gap={2} mb={2} mt={3}>
                <Typography variant="body2" color="textSecondary">
                  Confidence Levels Legend:{" "}
                </Typography>
                <Grid item style={{ width: "28px" }} />
                {/* Green Box */}
                <Box
                  style={{
                    backgroundColor: "green",
                    color: "white",
                    borderRadius: 4,
                    padding: "4px 12px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textAlign: "center",
                    minWidth: 30,
                  }}
                >
                  &gt; 90
                </Box>
                {/* Orange Box */}
                <Box
                  style={{
                    backgroundColor: "orange",
                    color: "white",
                    borderRadius: 4,
                    padding: "4px 12px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textAlign: "center",
                    minWidth: 50,
                    marginLeft: 16,
                  }}
                >
                  80 to 90
                </Box>
                {/* Red Box */}
                <Box
                  style={{
                    backgroundColor: "red",
                    color: "white",
                    borderRadius: 4,
                    padding: "4px 12px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textAlign: "center",
                    minWidth: 30,
                    marginLeft: 16,
                  }}
                >
                  &lt; 80
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Right side: PDF view */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              style={{
                height: "calc(100vh - 200px)",
                padding: 16,
                overflowY: "auto",
              }}
            >
              <Box
                style={{
                  height: 500,
                  backgroundColor: "#f5f5f5",
                  border: "1px dashed #ccc",
                }}
              >
                {schrockenCustomFileID ? (
                  <PDFViewer schrockenCustomFileID={schrockenCustomFileID} />
                ) : (
                  <Box
                    style={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography>No PDF available</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            padding: 24,
            boxShadow: 24,
            borderRadius: 8,
            minWidth: 600,
          }}
        >
          <Typography variant="h6" gutterBottom>
            {modalData.field} - Field Data History
          </Typography>

          <Table>
            <TableHead
              style={{ border: "1px solid grey", backgroundColor: "#7777" }}
            >
              <TableRow
                style={{ border: "1px solid grey", backgroundColor: "#7777" }}
              >
                <TableCell>
                  <b>Timestamp</b>
                </TableCell>
                <TableCell>
                  <b>Before Value</b>
                </TableCell>
                <TableCell>
                  <b>After Value</b>
                </TableCell>
                <TableCell>
                  <b>Updated By</b>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody style={{ border: "1px solid grey" }}>
              <TableRow>
                <TableCell>{modalData.timestamp}</TableCell>
                <TableCell>{modalData.before}</TableCell>
                <TableCell>{modalData.after}</TableCell>
                <TableCell>{modalData.updatedBy}</TableCell>
              </TableRow>
            </TableBody>
          </Table>

          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenModal(false)}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default DigitizedCommercialInvoice;
